import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { ordersApi, type Order } from "../../api/orders";
import type { ContractErpTerms } from "../../api/contracts";
import type { ApiError } from "../../api/client";
import { Select } from "../Select";
import { useToast } from "../Toast";
import { formatMoney } from "../../lib/format";

/** The length of a place of delivery in the ERP. */
const ERP_PLACE = 50;

/**
 * Bankom's terms of the ERP pro forma invoice an order becomes once approved. They are picked from the ERP's lists, as
 * a contract picks them, and are fixed at the approval, when the order goes to the ERP as it is; afterwards the card
 * shows them with the number of the ERP document.
 */
export function OrderErpTerms({ order, onSaved }: { order: Order; onSaved: (order: Order) => void }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const editable = order.status === "DRAFT" || order.status === "SUBMITTED" || order.status === "IN_REVIEW";
  const domestic = (order.companyCountry ?? "").trim().toUpperCase() === "RS";
  const [lists, setLists] = useState<ContractErpTerms>({ incoterms: [], paymentTerms: [], deliveryPlaces: [] });
  const [incoterm, setIncoterm] = useState("");
  const [place, setPlace] = useState("");
  const [payment, setPayment] = useState("");
  const [transport, setTransport] = useState("");
  const [paidBy, setPaidBy] = useState<"BANKOM" | "CUSTOMER">("BANKOM");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (editable) ordersApi.erpTerms().then(setLists).catch(() => undefined);
  }, [editable]);

  useEffect(() => {
    setIncoterm(order.incoterm ?? "");
    setPlace(order.incotermPlace ?? "");
    setPayment(order.paymentTerms ?? "");
    setTransport(order.transportAmount != null ? String(order.transportAmount) : "");
    setPaidBy(order.transportPaidBy === "CUSTOMER" ? "CUSTOMER" : "BANKOM");
  }, [order.incoterm, order.incotermPlace, order.paymentTerms, order.transportAmount, order.transportPaidBy]);

  async function save() {
    setBusy(true);
    try {
      const updated = await ordersApi.update(order.id, {
        incoterm,
        incotermPlace: place,
        paymentTerms: payment,
        transportAmount: transport.trim() === "" ? 0 : Number(transport),
        transportPaidBy: paidBy,
      });
      onSaved(updated);
      toast.success(t("orderErp.saved", "Terms saved."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  const notInErp = t("orderErp.notInErp", "not on the ERP list");
  return (
    <section style={card}>
      <h2 style={title}>{t("orderErp.title", "For the ERP")}</h2>
      {editable ? (
        <>
          <p style={hint}>
            {t("orderErp.hint", "Terms of the pro forma invoice the order becomes in the ERP once approved. An empty one goes as none.")}
          </p>
          <label style={field}>
            <span style={label}>{t("orderErp.incoterm", "Incoterm")}</span>
            <Select<string>
              value={incoterm}
              onChange={setIncoterm}
              ariaLabel={t("orderErp.incoterm", "Incoterm")}
              placeholder={t("orderErp.incotermPick", "Pick an incoterm from the ERP")}
              options={withCurrent(incoterm, lists.incoterms.map((i) => ({ value: i.code, label: `${i.code} — ${i.name}` })), notInErp)}
            />
          </label>
          <label style={field}>
            <span style={label}>{t("orderErp.incotermPlace", "Place of delivery")}</span>
            {/* Places on the ERP's contracts, as the ERP writes them: picking one matches the ERP's own row. */}
            <input value={place} onChange={(e) => setPlace(e.target.value)} maxLength={ERP_PLACE}
                   list="order-erp-places" className="input" style={{ width: "100%" }} />
            <datalist id="order-erp-places">
              {lists.deliveryPlaces.map((m) => <option key={m} value={m} />)}
            </datalist>
          </label>
          <label style={field}>
            <span style={label}>{t("orderErp.paymentTerms", "Payment terms")}</span>
            <Select<string>
              value={payment}
              onChange={setPayment}
              ariaLabel={t("orderErp.paymentTerms", "Payment terms")}
              placeholder={t("orderErp.paymentPick", "Pick payment terms from the ERP")}
              options={withCurrent(payment, lists.paymentTerms.map((n) => ({ value: n, label: n })), notInErp)}
            />
          </label>
          <label style={field}>
            <span style={label}>{t("orderErp.transport", "Transport")}</span>
            {/* In the order's own currency and without VAT, as on the offer: VAT is added only where the customer pays. */}
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="number" min={0} step="0.01" value={transport} onChange={(e) => setTransport(e.target.value)}
                     className="input" style={{ flex: 1, minWidth: 0 }} />
              <span style={{ color: "var(--ink-3)", fontSize: 13, whiteSpace: "nowrap" }}>
                {order.currency}, {t("orderErp.withoutVat", "without VAT")}
              </span>
            </span>
            {!domestic && paidBy === "BANKOM" && (
              <small style={hint}>
                {t("orderErp.transportForeign", "A foreign pro forma invoice has no transport in the ERP, so it stays in the portal.")}
              </small>
            )}
          </label>
          <label style={field}>
            <span style={label}>{t("orderErp.transportPaidBy", "Who pays the transport")}</span>
            <Select<"BANKOM" | "CUSTOMER">
              value={paidBy}
              onChange={setPaidBy}
              ariaLabel={t("orderErp.transportPaidBy", "Who pays the transport")}
              options={[
                { value: "BANKOM", label: t("orderErp.transportBankom", "Bankom: its cost, spread over the lines") },
                { value: "CUSTOMER", label: t("orderErp.transportCustomer", "The customer: a line of the pro forma invoice") },
              ]}
            />
          </label>
          <div>
            <button type="button" className="btn" onClick={() => void save()} disabled={busy}>
              {t("orderErp.save", "Save terms")}
            </button>
          </div>
        </>
      ) : (
        // A label above its value, as in the fields: a document number side by side with a long label breaks.
        <dl style={facts}>
          {([
            [t("orderErp.proforma", "ERP pro forma invoice"),
              order.erpProformaNumber ?? t("orderErp.proformaPending", "Waiting for the ERP write")],
            ...(order.erpCustomerOrderNumber
              ? [[t("orderErp.customerOrder", "ERP customer order"), order.erpCustomerOrderNumber] as const]
              : []),
            [t("orderErp.incoterm", "Incoterm"), [order.incoterm, order.incotermPlace].filter(Boolean).join(" ") || "—"],
            [t("orderErp.paymentTerms", "Payment terms"), order.paymentTerms || "—"],
            [t("orderErp.transport", "Transport"),
              order.transportAmount != null
                ? `${formatMoney(order.transportAmount, order.currency, i18n.resolvedLanguage)}, ${t("orderErp.withoutVat", "without VAT")}`
                : "—"],
            [t("orderErp.transportPaidBy", "Who pays the transport"),
              order.transportPaidBy === "CUSTOMER"
                ? t("orderErp.transportCustomer", "The customer: a line of the pro forma invoice")
                : t("orderErp.transportBankom", "Bankom: its cost, spread over the lines")],
          ] as const).map(([name, text]) => (
            <div key={name}>
              <dt style={label}>{name}</dt>
              <dd style={value}>{text}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

/** The list with an empty choice first, and a value not on it kept visible and marked, so it can be changed. */
function withCurrent(value: string, options: { value: string; label: string }[], marker: string) {
  const offList = value !== "" && !options.some((o) => o.value === value) ? [{ value, label: `${value} (${marker})` }] : [];
  return [{ value: "", label: "—" }, ...offList, ...options];
}

const card: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 8,
};
const title: CSSProperties = { margin: 0, fontSize: 14, color: "var(--ink)" };
const hint: CSSProperties = { margin: 0, fontSize: 12, color: "var(--ink-3)" };
const field: CSSProperties = { display: "flex", flexDirection: "column", gap: 4, fontSize: 13, marginTop: 4 };
const label: CSSProperties = {
  display: "block", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const facts: CSSProperties = { display: "flex", flexDirection: "column", gap: 10, margin: 0 };
const value: CSSProperties = { margin: 0, fontSize: 13, color: "var(--ink)" };
